'use client'

import { cn } from '@brew/ui/cn'
import {
  type ComponentProps,
  type ComponentType,
  createContext,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useContext,
  useRef,
  useState,
} from 'react'
import type { LucideIcon } from './icons'

/** Any icon component: a kit glyph or a product one (`*Icon`, glyph sets). */
export type BrewIcon =
  | LucideIcon
  | ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>

/**
 * Interactive controls for skinned dialogs: radio cards, segmented pill
 * tracks, file drop zones. Radio-like controls share one keyboard model —
 * a single tab stop, arrows/Home/End move and select (like native radios).
 */

const RADIO_SELECTOR = '[role="radio"]:not(:disabled)'

/** Arrow / Home / End inside a radiogroup: focus and select the target. */
function moveRadioFocus(event: KeyboardEvent<HTMLElement>) {
  const keys = [
    'ArrowRight',
    'ArrowDown',
    'ArrowLeft',
    'ArrowUp',
    'Home',
    'End',
  ]
  if (!keys.includes(event.key)) {
    return
  }
  const radios = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(RADIO_SELECTOR)
  )
  if (radios.length === 0) {
    return
  }
  const current = radios.indexOf(document.activeElement as HTMLElement)
  let next = current
  if (event.key === 'Home') {
    next = 0
  } else if (event.key === 'End') {
    next = radios.length - 1
  } else {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1
    next = (Math.max(current, 0) + step + radios.length) % radios.length
  }
  event.preventDefault()
  radios[next]?.focus()
  radios[next]?.click()
}

/** One tab stop: the checked option, or every option while none is. */
function radioTabIndex(checked: boolean, anyChecked: boolean) {
  if (checked) {
    return 0
  }
  return anyChecked ? -1 : 0
}

const ChoiceGroupContext = createContext({ anyChecked: true })

/**
 * Radio cards: a decision with consequences. Arrow keys move the choice;
 * `as="fieldset"` (with `disabled`) disables every card at once.
 */
export function BrewChoiceGroup({
  label,
  labelId,
  orientation = 'vertical',
  as = 'div',
  disabled,
  anyChecked = true,
  className,
  children,
  ...props
}: Omit<ComponentProps<'div'>, 'role'> & {
  label?: ReactNode
  /** Id for the label so the radiogroup is named by it. */
  labelId?: string
  orientation?: 'vertical' | 'horizontal'
  as?: 'div' | 'fieldset'
  disabled?: boolean
  /** False while nothing is selected yet: every card stays a tab stop. */
  anyChecked?: boolean
}) {
  const Root = as
  const labelClass = 'font-medium text-[var(--dlg-ink)] text-dialog-meta'
  let labelNode: ReactNode = null
  if (label && as === 'fieldset') {
    labelNode = (
      <legend id={labelId} className={cn('mb-2', labelClass)}>
        {label}
      </legend>
    )
  } else if (label) {
    labelNode = (
      <div id={labelId} className={labelClass}>
        {label}
      </div>
    )
  }
  return (
    <Root
      disabled={as === 'fieldset' ? disabled : undefined}
      className={cn(
        'flex min-w-0 flex-col gap-2',
        as === 'fieldset' && 'm-0 border-0 p-0'
      )}
    >
      {labelNode}
      <ChoiceGroupContext.Provider value={{ anyChecked }}>
        <div
          role="radiogroup"
          aria-labelledby={label ? labelId : undefined}
          onKeyDown={moveRadioFocus}
          className={cn(
            'flex gap-2',
            orientation === 'horizontal' ? 'flex-col sm:flex-row' : 'flex-col',
            className
          )}
          {...props}
        >
          {children}
        </div>
      </ChoiceGroupContext.Provider>
    </Root>
  )
}

export function BrewChoiceCard({
  checked,
  onSelect,
  title,
  description,
  extra,
  tone = 'default',
  disabled,
  className,
  ref,
  ...props
}: Omit<ComponentProps<'button'>, 'title' | 'onSelect' | 'role'> & {
  checked: boolean
  onSelect: () => void
  title: ReactNode
  description?: ReactNode
  /** A third line under the description (what else the choice removes). */
  extra?: ReactNode
  tone?: 'default' | 'danger'
  ref?: Ref<HTMLButtonElement>
}) {
  const { anyChecked } = useContext(ChoiceGroupContext)
  const isDanger = tone === 'danger' && checked
  const cardClass = isDanger
    ? 'bg-[var(--dlg-danger-wash)] shadow-[0_0_0_1.5px_var(--dlg-danger)]'
    : 'bg-[var(--dlg-accent-wash)] shadow-[0_0_0_1.5px_var(--dlg-accent)]'
  const dotClass = isDanger
    ? 'shadow-[inset_0_0_0_5px_var(--dlg-danger)]'
    : 'shadow-[inset_0_0_0_5px_var(--dlg-accent)]'
  return (
    <button
      ref={ref}
      type="button"
      role="radio"
      aria-checked={checked}
      tabIndex={radioTabIndex(checked, anyChecked)}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex min-w-0 flex-1 items-start gap-3 rounded-[14px] px-4 py-3.5 text-left outline-none transition-[box-shadow,background-color] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-60',
        tone === 'danger'
          ? 'focus-visible:ring-[var(--dlg-danger)]/30'
          : 'focus-visible:ring-[var(--dlg-accent)]/30',
        checked
          ? cardClass
          : 'bg-[var(--dlg-surface)] shadow-[0_0_0_1px_var(--dlg-line)] hover:shadow-[0_0_0_1px_var(--dlg-faint)]',
        className
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-px size-[18px] shrink-0 rounded-full bg-[var(--dlg-surface)] transition-shadow duration-quick',
          checked ? dotClass : 'shadow-[inset_0_0_0_1.5px_var(--dlg-faint)]'
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="font-medium text-[var(--dlg-ink)] text-sm leading-[18px]">
          {title}
        </span>
        {description ? (
          <span className="text-[var(--dlg-body)] text-xs leading-[17px]">
            {description}
          </span>
        ) : null}
        {extra ? (
          <span className="text-[var(--dlg-muted)] text-xs leading-[17px]">
            {extra}
          </span>
        ) : null}
      </span>
    </button>
  )
}

export type BrewSegmentedOption<TValue extends string> = Readonly<{
  value: TValue
  label: ReactNode
  icon?: BrewIcon
  disabled?: boolean
}>

/**
 * A pill track of mutually exclusive options (source switch, type picker,
 * AND/OR, crop ratio). One radiogroup, one selected style, arrow keys.
 */
export function BrewSegmented<TValue extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  fullWidth = false,
  columns,
  wrap = false,
  disabled,
  className,
  ...aria
}: {
  value: TValue
  onValueChange: (value: TValue) => void
  options: ReadonlyArray<BrewSegmentedOption<TValue>>
  size?: 'sm' | 'md'
  fullWidth?: boolean
  /** A fixed grid instead of a single row (4 field types → 2×2 on phones). */
  columns?: 2 | 3 | 4
  wrap?: boolean
  disabled?: boolean
  className?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}) {
  const anyChecked = options.some((option) => option.value === value)
  return (
    <div
      role="radiogroup"
      onKeyDown={moveRadioFocus}
      className={cn(
        'gap-1 rounded-full bg-[var(--dlg-tile)] p-1 shadow-[inset_0_0_0_1px_var(--dlg-divider)]',
        columns ? 'grid rounded-[14px]' : 'flex',
        {
          'grid-cols-2': columns === 2,
          'grid-cols-2 sm:grid-cols-3': columns === 3,
          'grid-cols-2 sm:grid-cols-4': columns === 4,
          'w-full': fullWidth || columns,
          'w-fit': !(fullWidth || columns),
          'flex-wrap rounded-[18px]': wrap,
        },
        className
      )}
      {...aria}
    >
      {options.map((option) => {
        const checked = option.value === value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={radioTabIndex(checked, anyChecked)}
            disabled={disabled || option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap font-medium outline-none transition-[background-color,color,box-shadow] focus-visible:ring-2 focus-visible:ring-[var(--dlg-accent)]/40 disabled:cursor-not-allowed disabled:opacity-50',
              columns ? 'rounded-[10px]' : 'rounded-full',
              size === 'sm'
                ? 'h-6 px-2.5 text-xs'
                : 'h-8 px-3.5 text-dialog-meta',
              (fullWidth || columns) && 'flex-1',
              checked
                ? 'bg-[var(--dlg-surface)] text-[var(--dlg-ink)] shadow-[var(--dlg-lift-segment)]'
                : 'text-[var(--dlg-muted)] hover:text-[var(--dlg-ink)]'
            )}
          >
            {Icon ? (
              <Icon className="size-3.5 shrink-0" aria-hidden="true" />
            ) : null}
            <span className="truncate">{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/** Drag-over state and drop handling for any region that accepts files. */
export function useBrewFileDrop({
  onFiles,
  disabled = false,
}: {
  onFiles: (files: Array<File>) => void
  disabled?: boolean
}) {
  const [isDragging, setIsDragging] = useState(false)
  const depth = useRef(0)
  const dropProps = {
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      if (disabled) {
        return
      }
      event.preventDefault()
      depth.current += 1
      setIsDragging(true)
    },
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!disabled) {
        event.preventDefault()
      }
    },
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) {
        setIsDragging(false)
      }
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      depth.current = 0
      setIsDragging(false)
      if (disabled) {
        return
      }
      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) {
        onFiles(files)
      }
    },
  }
  return { isDragging, dropProps }
}

/**
 * A file tile: click or drop. Owns the hidden input and the drag state.
 * `compact` once files are listed below it; `grid` as one cell of a grid.
 */
export function BrewDropZone({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  allowDrop = true,
  icon: Icon,
  title,
  caption,
  variant = 'tile',
  inputRef,
  inputId,
  className,
}: {
  onFiles: (files: Array<File>) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  allowDrop?: boolean
  icon?: BrewIcon
  title: ReactNode
  caption?: ReactNode
  variant?: 'tile' | 'compact' | 'grid'
  inputRef?: Ref<HTMLInputElement>
  inputId?: string
  className?: string
}) {
  const localInput = useRef<HTMLInputElement | null>(null)
  const { isDragging, dropProps } = useBrewFileDrop({
    onFiles,
    disabled: disabled || !allowDrop,
  })
  const setInput = (node: HTMLInputElement | null) => {
    localInput.current = node
    if (typeof inputRef === 'function') {
      inputRef(node)
    } else if (inputRef) {
      inputRef.current = node
    }
  }
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => localInput.current?.click()}
        {...(allowDrop ? dropProps : {})}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-2xl bg-[var(--dlg-tile)] text-center outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-[var(--dlg-accent)] disabled:cursor-not-allowed disabled:opacity-60',
          isDragging
            ? 'shadow-[inset_0_0_0_1.5px_var(--dlg-accent)]'
            : 'shadow-[inset_0_0_0_1px_var(--dlg-line)] hover:shadow-[inset_0_0_0_1px_var(--dlg-faint)]',
          {
            'h-[132px] px-6': variant === 'tile',
            'px-4 py-4': variant === 'compact',
            'h-24 rounded-[14px] px-3': variant === 'grid',
          },
          className
        )}
      >
        {Icon && variant !== 'compact' ? (
          <span className="flex size-10 items-center justify-center rounded-full bg-[var(--dlg-surface)] text-[var(--dlg-accent-deep)] shadow-[var(--dlg-lift-sm)]">
            <Icon className="size-[18px]" aria-hidden="true" />
          </span>
        ) : null}
        <span className="font-medium text-[var(--dlg-ink)] text-sm leading-5">
          {title}
        </span>
        {caption ? (
          <span className="text-[var(--dlg-muted)] text-xs leading-4">
            {caption}
          </span>
        ) : null}
      </button>
      <input
        ref={setInput}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? [])
          event.currentTarget.value = ''
          if (files.length > 0) {
            onFiles(files)
          }
        }}
      />
    </>
  )
}

/** A picked file: icon, name, meta (format · size) and a quiet Remove. */
export function BrewFileChip({
  icon: Icon,
  name,
  meta,
  onRemove,
  removeLabel = 'Remove',
  removeAriaLabel,
  disabled,
  className,
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {
  icon: BrewIcon
  name: ReactNode
  meta?: ReactNode
  onRemove?: () => void
  removeLabel?: string
  /** Accessible name for Remove when the visible word is too bare. */
  removeAriaLabel?: string
  disabled?: boolean
}) {
  return (
    <div
      {...props}
      className={cn(
        'flex items-center gap-3 rounded-[14px] bg-[var(--dlg-tile)] px-3.5 py-3',
        className
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--dlg-surface)] text-[var(--dlg-accent-deep)] shadow-[var(--dlg-lift-sm)]">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-[var(--dlg-ink)] text-sm leading-5">
          {name}
        </span>
        {meta ? (
          <span className="truncate text-[var(--dlg-muted)] text-xs leading-4">
            {meta}
          </span>
        ) : null}
      </span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeAriaLabel}
          className="shrink-0 rounded-full px-2.5 py-1 font-medium text-[var(--dlg-body)] text-xs outline-none transition-colors hover:bg-[var(--dlg-surface)] hover:text-[var(--dlg-ink)] focus-visible:ring-2 focus-visible:ring-[var(--dlg-accent)] disabled:opacity-50"
        >
          {removeLabel}
        </button>
      ) : null}
    </div>
  )
}
